// IEA Site Survey Outbox: every save waits on the device until the cloud confirms it.
// Words: IEA_OFFLINE_SHELL_WORDS.md rev 3, sections 10 to 13 (approved 2026-10-14).
//
// Two layers.
// * Pure rules (this file, top): counts, refusal words, backup checks, photo names, the
//   survey-in-progress record. Unit tested: tests/unit/outbox.test.ts.
// * The device list (this file, bottom): an IndexedDB store that survives closing the app
//   and restarting the iPad. Firestore's own store sends the text; this list is how the
//   Outbox knows what is still waiting, and what goes into a backup.

// ---- What waits ----

export type EntryState = "waiting" | "sent" | "refused";

/** One saved record: an IEA Data card or a Connections row. Fixed ID, so retries never double. */
export interface RecordEntry {
  /** The Firestore path. Also the key. */
  path: string;
  company: string;
  siteNumber: string;
  assetId: string;
  /** Plain words for the Outbox list: "Housing card", "Way out". */
  card: string;
  data: Record<string, unknown>;
  savedAt: string;
  state: EntryState;
  reason: string;
  sentAt: string;
}

/** One photo. Uploads after its record is sent. A failed photo never blocks the text. */
export interface PhotoEntry {
  /** The Storage path. Also the key. */
  path: string;
  company: string;
  siteNumber: string;
  assetId: string;
  recordPath: string;
  contentType: string;
  blob: Blob;
  takenAt: string;
  state: EntryState;
  reason: string;
  /** Label PDFs for Drive only (storage rule 7): the badge stamp the rule checks. */
  meta?: Record<string, string>;
}

/** A label PDF waiting for Drive rides the photo list. It has no record to wait on and is never held. */
export function isDrivePdf(p: Pick<PhotoEntry, "path">): boolean {
  return p.path.includes("/driveOutbox/");
}

export interface OutboxCounts {
  waiting: number;
  sentToday: number;
  photosWaiting: number;
  refused: number;
}

export function outboxCounts(records: readonly RecordEntry[], photos: readonly (Pick<PhotoEntry, "state"> & Partial<Pick<PhotoEntry, "path">>)[], todayIso: string): OutboxCounts {
  const day = todayIso.slice(0, 10);
  // A label PDF for Drive counts as a waiting item, not a photo.
  const pdfs = photos.filter((p) => p.path !== undefined && isDrivePdf({ path: p.path }) && p.state !== "sent").length;
  return {
    waiting: records.filter((r) => r.state !== "sent").length + pdfs,
    sentToday: records.filter((r) => r.state === "sent" && r.sentAt.slice(0, 10) === day).length,
    photosWaiting: photos.filter((p) => p.state !== "sent" && !(p.path !== undefined && isDrivePdf({ path: p.path }))).length,
    refused: records.filter((r) => r.state === "refused").length,
  };
}

/** Why the cloud refused a save, in words. Only a refusal stops retries; no signal never does. */
export function refusedWords(code: string): string | null {
  if (code === "permission-denied") return "Refused: your badge is switched off, has no IEA Site Survey switch, or does not cover this site. Tell your admin.";
  if (code === "invalid-argument" || code === "failed-precondition") return "Refused: the cloud would not take this record as written.";
  return null;
}

// ---- Photo names (section 11) ----

/** `{ASSET_ID}_{YYYYMMDD}_{type}.jpg`. A second of the same type that day gets _2, then _3. */
export function photoPath(company: string, siteNumber: string, assetId: string, ymd: string, type: string, taken: ReadonlySet<string>, ext = "jpg"): string {
  const base = `companies/${company}/sites/${siteNumber}/ieaPhotos/${assetId}_${ymd.replace(/-/g, "")}_${type.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
  let n = 1;
  let path = `${base}.${ext}`;
  while (taken.has(path)) path = `${base}_${++n}.${ext}`;
  return path;
}

// ---- Backup (section 12) ----

export const BACKUP_KIND = "safetag-survey-backup";

export interface BackupPhoto extends Omit<PhotoEntry, "blob"> {
  base64: string;
}

export interface Backup {
  kind: typeof BACKUP_KIND;
  version: 1;
  company: string;
  exportedAt: string;
  deviceName: string;
  records: RecordEntry[];
  photos: BackupPhoto[];
}

/** "SafeTag-Springfield-2026-10-14.json". */
export function backupFileName(siteName: string, isoNow: string): string {
  return `SafeTag-${siteName.replace(/[^A-Za-z0-9]+/g, "-")}-${isoNow.slice(0, 10)}.json`;
}

/** Checks a backup file before anything is sent. Another company or an uncovered site is refused. */
export function checkBackup(raw: unknown, company: string, coveredSites: readonly string[]): { ok: true; backup: Backup } | { ok: false; error: string } {
  const b = raw as Partial<Backup> | null;
  if (!b || b.kind !== BACKUP_KIND || b.version !== 1 || !Array.isArray(b.records) || !Array.isArray(b.photos)) {
    return { ok: false, error: "That file is not a SafeTag survey backup." };
  }
  if (b.company !== company) return { ok: false, error: "That backup belongs to another company." };
  const covers = (s: string) => coveredSites.includes("ALL") || coveredSites.includes(s);
  const bad = [...b.records, ...b.photos].find((x) => !x || typeof x.path !== "string" || !covers(String(x.siteNumber)) || !String(x.path).startsWith(`companies/${company}/sites/${x.siteNumber}/`));
  if (bad) return { ok: false, error: `That backup holds a site your badge does not cover (${String(bad?.siteNumber ?? "unknown")}).` };
  return { ok: true, backup: b as Backup };
}

/**
 * A backup record is sent only when the cloud does not already hold a newer one. Records carry
 * Recorded At. A backup never overwrites a later edit made on another device.
 */
export function backupWins(cloudRecordedAt: string | null, backupRecordedAt: string): boolean {
  return cloudRecordedAt === null || backupRecordedAt > cloudRecordedAt;
}

// ---- Survey in progress (section 13) ----

export interface WalkStatusDoc {
  "Site Number": string;
  "Badge ID": string;
  "Holder Name": string;
  "Device ID": string;
  "Device Name": string;
  State: "Open" | "Ended";
  "Started At": string;
  "Last Seen": string;
  Waiting: number;
}

/** Another device's open survey of this site, for the pre-survey check. Null when clear. */
export function otherDeviceFrom(doc: WalkStatusDoc | null, myDeviceId: string): { deviceName: string; waiting: number; lastSeen: string } | null {
  if (!doc || doc.State !== "Open" || doc["Device ID"] === myDeviceId) return null;
  const t = new Date(doc["Last Seen"]);
  const lastSeen = Number.isNaN(t.getTime()) ? doc["Last Seen"] : t.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return { deviceName: doc["Device Name"] || "Another device", waiting: doc.Waiting, lastSeen };
}

// ---- Sync log (section 12): the last 50 events ----

export interface LogLine {
  at: string;
  text: string;
}

export function addLog(log: readonly LogLine[], text: string, at: string): LogLine[] {
  return [{ at, text }, ...log].slice(0, 50);
}

// ---- The device list (IndexedDB) ----

const DB_NAME = "safetag-survey";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("records")) db.createObjectStore("records", { keyPath: "path" });
      if (!db.objectStoreNames.contains("photos")) db.createObjectStore("photos", { keyPath: "path" });
      if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(store: "records" | "photos" | "meta", mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = run(t.objectStore(store));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export const deviceList = {
  records: (): Promise<RecordEntry[]> => tx("records", "readonly", (s) => s.getAll() as IDBRequest<RecordEntry[]>),
  photos: (): Promise<PhotoEntry[]> => tx("photos", "readonly", (s) => s.getAll() as IDBRequest<PhotoEntry[]>),
  /** One photo by its path, without reading every photo's file. */
  photo: (path: string): Promise<PhotoEntry | undefined> => tx("photos", "readonly", (s) => s.get(path) as IDBRequest<PhotoEntry | undefined>),
  putRecord: (r: RecordEntry): Promise<IDBValidKey> => tx("records", "readwrite", (s) => s.put(r)),
  /** A Model Tag another iPad took first: the renumbered one replaces it (survey-tags.ts). */
  deleteRecord: (path: string): Promise<undefined> => tx("records", "readwrite", (s) => s.delete(path) as IDBRequest<undefined>),
  putPhoto: (p: PhotoEntry): Promise<IDBValidKey> => tx("photos", "readwrite", (s) => s.put(p)),
  /** A photo deleted before it uploaded: it leaves the iPad and never uploads (IEA_PHOTO_FLOW_WORDS 3.4). */
  deletePhoto: (path: string): Promise<undefined> => tx("photos", "readwrite", (s) => s.delete(path) as IDBRequest<undefined>),
  log: async (): Promise<LogLine[]> => ((await tx("meta", "readonly", (s) => s.get("log") as IDBRequest<LogLine[] | undefined>)) ?? []),
  /** One read-and-write step, so two lines written close together never lose one. */
  addLog: async (text: string): Promise<void> => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction("meta", "readwrite");
      const st = t.objectStore("meta");
      const get = st.get("log");
      get.onsuccess = () => st.put(addLog((get.result as LogLine[] | undefined) ?? [], text, new Date().toISOString()), "log");
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  },
};
